import {
  AppError,
  CommonErrorsKey,
  SupportedChainId,
  isSupportedChainId,
} from "@eulerxyz/core-sdk"
import type { Account, Chain, Client, Transport } from "viem"

export type PendingResolve<value> = (value: value) => void

export abstract class BaseAggregator<
  value,
  transport extends Transport = Transport,
  chain extends Chain | undefined = Chain | undefined,
  account extends Account | undefined = Account | undefined,
> {
  /**
   * The client to use for making batched contract transactions.
   * @public
   */
  client: Client<transport, chain, account>

  /**
   * The chain id to use for making batched contract transactions.
   * @protected
   */
  protected chainId: SupportedChainId

  /**
   * An array of pending resolve functions for evc batch requests.
   * @protected
   */
  protected pendingResolves: PendingResolve<value>[] = []

  /**
   * An array of pending reject functions for evc batch requests.
   * @protected
   */
  protected pendingRejects: ((error: Error) => void)[] = []

  /**
   * A timer to schedule evc batch execution.
   * @private
   */
  #timer?: ReturnType<typeof setTimeout> = undefined

  /**
   * Create a new instance of the BaseAggregator class.
   * @param client - The client to use for making batched contract transactions.
   */
  constructor(client: Client<transport, chain, account>) {
    this.client = client
    if (!client.chain?.id || !isSupportedChainId(client.chain.id)) {
      throw new AppError(
        CommonErrorsKey.ChainNotSupported,
        "Chain not supported",
      )
    }
    this.chainId = client.chain.id
  }

  /**
   * Internal method to schedule the execution of a batch.
   * @protected
   */
  protected scheduleBatch() {
    if (this.#timer === undefined) {
      this.#timer = setTimeout(async () => {
        this.#timer = undefined
        await this.makeBatchNow()
      }, 0)
    }
  }

  /**
   * Internal method to schedule the execution of a batch.
   * @protected
   */
  protected async makeBatchNow() {
    throw new Error("Not implemented")
  }
}
